import React, { FC } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { MAX_PROFESSIONAL_ROWS } from "../../PaygapAnalysisParameters";
import type {
  AnalysisParameters,
  ProfessionalPositionItem,
} from "../../../../hooks";

import { Button, TextField } from "@components/base";

interface ProfessionalRowsFormProps {
  professionalPositions: ProfessionalPositionItem[];
  parameters: AnalysisParameters;
  onProfessionalPositionChange: (
    index: number,
    field: keyof ProfessionalPositionItem,
    value: string | number,
  ) => void;
  onAddProfessionalPosition: () => void;
  onRemoveProfessionalPosition: (index: number) => void;
}

export const ProfessionalRowsForm: FC<ProfessionalRowsFormProps> = ({
  professionalPositions,
  parameters,
  onProfessionalPositionChange,
  onAddProfessionalPosition,
  onRemoveProfessionalPosition,
}) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {professionalPositions.map((item, index) => (
        <Box
          key={`professional-row-${index}`}
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "120px 1fr 64px" }}
          gap={1}
          alignItems="center"
        >
          <TextField
            label={t("paygap.referentials.id")}
            type="number"
            value={item.id}
            error={
              !!item.id.trim() &&
              parameters.professionalPositions.filter(
                (row) => row.id.trim() === item.id.trim(),
              ).length > 1
            }
            helperText={
              !!item.id.trim() &&
              parameters.professionalPositions.filter(
                (row) => row.id.trim() === item.id.trim(),
              ).length > 1
                ? t("paygap.referentials.uniqueId")
                : undefined
            }
            onChange={(e) =>
              onProfessionalPositionChange(index, "id", e.target.value)
            }
            fullWidth
          />
          <TextField
            label={t("paygap.referentials.name")}
            value={item.name}
            onChange={(e) =>
              onProfessionalPositionChange(index, "name", e.target.value)
            }
            fullWidth
          />
          <IconButton
            aria-label={t("paygap.referentials.remove")}
            onClick={() => onRemoveProfessionalPosition(index)}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box display="flex" alignItems="center" gap={2}>
        <Button
          variant="outlined"
          onClick={onAddProfessionalPosition}
          disabled={professionalPositions.length >= MAX_PROFESSIONAL_ROWS}
        >
          {t("paygap.referentials.addRow")}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {`${professionalPositions.length}/${MAX_PROFESSIONAL_ROWS}`} ·{" "}
          {t("paygap.referentials.professionalLimit")}
        </Typography>
      </Box>
    </Box>
  );
};
